import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from '../lib/icons';
import { isVideoUrl } from '../lib/image-slots-client';

export interface CarouselItem {
  url: string;
  alt: string;
}

interface MediaCarouselProps {
  items: CarouselItem[];
  objectFit?: 'cover' | 'contain';
  primaryStyle?: React.CSSProperties;
  eager?: boolean;
  imgClassName?: string;
}

export default function MediaCarousel({
  items,
  objectFit = 'cover',
  primaryStyle,
  eager = false,
  imgClassName = '',
}: MediaCarouselProps) {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef(0);

  const count = items.length;

  const prev = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setCurrent(i => (i === 0 ? count - 1 : i - 1));
    },
    [count],
  );

  const next = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setCurrent(i => (i === count - 1 ? 0 : i + 1));
    },
    [count],
  );

  // Keyboard navigation (only when the element has focus-within)
  useEffect(() => {
    if (count <= 1) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count, prev, next]);

  if (count === 0) return null;

  const renderMedia = (item: CarouselItem, idx: number) => {
    const style: React.CSSProperties =
      idx === 0 && primaryStyle
        ? { objectFit, ...primaryStyle }
        : { objectFit };

    if (isVideoUrl(item.url)) {
      return (
        <video
          key={item.url}
          src={item.url}
          autoPlay
          muted
          loop
          playsInline
          className={`w-full h-full ${imgClassName}`}
          style={style}
        />
      );
    }
    return (
      <img
        key={item.url}
        src={item.url}
        alt={item.alt}
        loading={idx === 0 && eager ? 'eager' : 'lazy'}
        className={`w-full h-full ${imgClassName}`}
        style={style}
      />
    );
  };

  // Single item — no carousel chrome
  if (count === 1) {
    return <>{renderMedia(items[0], 0)}</>;
  }

  return (
    <div
      className="relative w-full h-full group/carousel overflow-hidden"
      onTouchStart={e => { touchStart.current = e.targetTouches[0].clientX; }}
      onTouchEnd={e => {
        const diff = touchStart.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
      }}
    >
      {/* Slides — stack and fade */}
      {items.map((item, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-300 ${
            idx === current ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
          }`}
          aria-hidden={idx !== current}
        >
          {renderMedia(item, idx)}
        </div>
      ))}

      {/* Prev / Next arrows — appear on hover */}
      <button
        type="button"
        onClick={prev}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/85 text-white rounded-full p-1.5
          opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-200 focus:opacity-100"
        aria-label="Previous"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={next}
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/85 text-white rounded-full p-1.5
          opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-200 focus:opacity-100"
        aria-label="Next"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {/* Dot pagination */}
      <div
        className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10"
        onClick={e => e.stopPropagation()}
      >
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={e => { e.stopPropagation(); setCurrent(i); }}
            className={`rounded-full transition-all duration-200 ${
              i === current
                ? 'bg-[#39FF14] w-3 h-1.5'
                : 'bg-white/50 hover:bg-white/80 w-1.5 h-1.5'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Slide counter */}
      <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-mono px-2 py-0.5 rounded-full z-10 pointer-events-none">
        {current + 1}/{count}
      </div>
    </div>
  );
}
